import type { FlatfinderPoint, FlatfinderRegion } from 'realitiq-utils/src/flatfinder';

export type AreaEdgeCandidate = {
  region: FlatfinderRegion;
  index: number;
  point: FlatfinderPoint;
  hovered: boolean;
};

export type PolygonEditorView = { x: number; y: number; zoom: number };

export type AreaDrag = {
  region: FlatfinderRegion;
  vertex: number | null;
  start: FlatfinderPoint;
  points: FlatfinderPoint[];
};

export type PinchGesture = {
  distance: number;
  center: FlatfinderPoint;
  view: PolygonEditorView;
};

export type PendingAreaEdge = {
  edge: AreaEdgeCandidate;
  insert: boolean;
};

const AREA_EDGE_HIT_RADIUS = 40;

export function convertClientPointToElementPoint(
  element: SVGGraphicsElement | null,
  clientPoint: FlatfinderPoint
) {
  const matrix = element?.getScreenCTM();
  if (!matrix) return { x: 0, y: 0 };

  const point = new DOMPoint(clientPoint.x, clientPoint.y).matrixTransform(matrix.inverse());
  return { x: point.x, y: point.y };
}

export function convertClientPointToNormalizedImagePoint(
  imageElement: SVGGElement | null,
  clientPoint: FlatfinderPoint,
  imageWidth: number,
  imageHeight: number
) {
  const point = convertClientPointToElementPoint(imageElement, clientPoint);
  return { x: point.x / imageWidth, y: point.y / imageHeight };
}

export function isPointInsideNormalizedImage(point: FlatfinderPoint) {
  return point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1;
}

export function calculatePointDistance(first: FlatfinderPoint, second: FlatfinderPoint) {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

export function calculatePointBetweenPointers(first: FlatfinderPoint, second: FlatfinderPoint) {
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
}

export function findNearestSelectedAreaEdge({
  imageElement,
  clientPoint,
  selectedArea,
  imageWidth,
  imageHeight,
}: {
  imageElement: SVGGElement | null;
  clientPoint: FlatfinderPoint;
  selectedArea: FlatfinderRegion | undefined;
  imageWidth: number;
  imageHeight: number;
}): AreaEdgeCandidate | null {
  const matrix = imageElement?.getScreenCTM();
  if (!matrix || !selectedArea) return null;

  let nearestEdge: AreaEdgeCandidate | null = null;
  let nearestDistance = AREA_EDGE_HIT_RADIUS;

  for (let index = 0; index < selectedArea.points.length; index++) {
    const startPoint = selectedArea.points[index];
    const endPoint = selectedArea.points[(index + 1) % selectedArea.points.length];
    const start = new DOMPoint(
      startPoint.x * imageWidth,
      startPoint.y * imageHeight
    ).matrixTransform(matrix);
    const end = new DOMPoint(endPoint.x * imageWidth, endPoint.y * imageHeight).matrixTransform(
      matrix
    );
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const squaredLength = deltaX * deltaX + deltaY * deltaY;
    if (!squaredLength) continue;

    const progress = Math.max(
      0,
      Math.min(
        1,
        ((clientPoint.x - start.x) * deltaX + (clientPoint.y - start.y) * deltaY) / squaredLength
      )
    );
    const edgeLength = Math.sqrt(squaredLength);
    if (progress * edgeLength < 6 || (1 - progress) * edgeLength < 6) {
      continue;
    }

    const distance = Math.hypot(
      clientPoint.x - start.x - progress * deltaX,
      clientPoint.y - start.y - progress * deltaY
    );
    if (distance >= nearestDistance) continue;

    nearestDistance = distance;
    nearestEdge = {
      region: selectedArea,
      index,
      point: {
        x: startPoint.x + progress * (endPoint.x - startPoint.x),
        y: startPoint.y + progress * (endPoint.y - startPoint.y),
      },
      hovered: distance < 20,
    };
  }

  return nearestEdge;
}

export function moveAreaVertex(
  area: FlatfinderRegion,
  vertexIndex: number,
  destination: FlatfinderPoint
) {
  const clamp = (value: number) => Math.min(1, Math.max(0, value));
  return area.points.map((point, index) =>
    index === vertexIndex ? { x: clamp(destination.x), y: clamp(destination.y) } : point
  );
}

export function moveAreaWithinImage(
  area: FlatfinderRegion,
  dragStart: FlatfinderPoint,
  destination: FlatfinderPoint
) {
  const horizontalPoints = area.points.map((point) => point.x);
  const verticalPoints = area.points.map((point) => point.y);
  const deltaX = Math.max(
    -Math.min(...horizontalPoints),
    Math.min(1 - Math.max(...horizontalPoints), destination.x - dragStart.x)
  );
  const deltaY = Math.max(
    -Math.min(...verticalPoints),
    Math.min(1 - Math.max(...verticalPoints), destination.y - dragStart.y)
  );

  return area.points.map((point) => ({
    x: point.x + deltaX,
    y: point.y + deltaY,
  }));
}

export function calculateZoomedView(
  currentView: PolygonEditorView,
  focusPoint: FlatfinderPoint,
  requestedZoom: number
) {
  const zoom = Math.max(0.5, Math.min(8, requestedZoom));
  return {
    zoom,
    x: focusPoint.x - ((focusPoint.x - currentView.x) * zoom) / currentView.zoom,
    y: focusPoint.y - ((focusPoint.y - currentView.y) * zoom) / currentView.zoom,
  };
}

export function calculatePinchZoomedView(
  initialView: PolygonEditorView,
  initialCenter: FlatfinderPoint,
  currentCenter: FlatfinderPoint,
  requestedZoom: number
) {
  const zoom = Math.max(0.5, Math.min(8, requestedZoom));
  return {
    zoom,
    x: currentCenter.x - ((initialCenter.x - initialView.x) * zoom) / initialView.zoom,
    y: currentCenter.y - ((initialCenter.y - initialView.y) * zoom) / initialView.zoom,
  };
}
